<template>
  <div>
    <portal to="body-end">
      <b-sidebar
          id="sidebar-add-new-event"
          sidebar-class="sidebar-xl crs-panel"
          v-model="sidebar.visible"
          bg-variant="white"
          shadow
          backdrop
          no-header
          width="1200px"
          right
          @change="(val) => $emit('update:is-event-handler-sidebar-active', val)"
      >
        <!-- ── header: guest name is the headline, status is a word ── -->
        <div class="crs-head">
          <div v-if="loading == null && contract != null" class="crs-head__main">
            <div class="crs-head__top">
              <span class="crs-head__name">{{ contract.client.firstname }} {{ contract.client.lastname }}</span>
              <span v-if="contract.status" class="crs-head__status" :class="`crs-head__status--${statusTone}`">{{ contract.status }}</span>
            </div>
            <div class="crs-head__sub">
              <span class="tabular">{{ contract.moveDate | moment('DD.MM.YYYY') }} → {{ contract.endDate | moment('DD.MM.YYYY') }}</span>
              <span v-if="property">{{ property.address }} {{ property.house }}, {{ property.city }}</span>
              <span v-if="contract.bnbStyleRent && contract.bnbStyleRent.platform">{{ contract.bnbStyleRent.platform }}</span>
            </div>
          </div>
          <div v-else class="crs-head__main">
            <span class="crs-head__name">{{ msg('Loading') }}…</span>
          </div>
          <div class="crs-head__tools">
            <a :href="`/real_estate/contracts/rent/${contractId}`" target="_blank" class="crs-tool"
               v-b-tooltip :title="msg('Open in new tab')">
              <i class="fa-solid fa-arrow-up-right-from-square"/>
            </a>
            <button type="button" class="crs-tool" @click="sidebar.visible = false"><i class="fa-solid fa-xmark"/></button>
          </div>
        </div>

        <!-- ── content ── -->
        <div v-if="loading==null && contractId != null" class="crs-body">
          <b-tabs v-model="tabsIndex" nav-class="crs-nav">
            <!-- Contract -->
            <b-tab :title="msg('Contract')" active>
              <div v-if="contract != null" class="crs-tab">
                <div class="crs-stay">
                  <div class="crs-stay__cell">
                    <span>{{ $t('roomBoard.checkIn') }}</span>
                    <b class="tabular">{{ contract.moveDate | moment('DD.MM.YYYY') }}</b>
                    <small>{{ contract.moveDate | moment('dddd') }}</small>
                  </div>
                  <i class="fas fa-arrow-right-long crs-stay__arrow"/>
                  <div class="crs-stay__cell">
                    <span>{{ $t('roomBoard.checkOut') }}</span>
                    <b class="tabular">{{ contract.endDate | moment('DD.MM.YYYY') }}</b>
                    <small>{{ contract.endDate | moment('dddd') }}</small>
                  </div>
                  <div v-if="contract.bnbStyleRent" class="crs-stay__cell">
                    <span>{{ $t('roomBoard.nights') }}</span>
                    <b class="tabular">{{ contract.bnbStyleRent.totalNights }}</b>
                  </div>
                  <div class="crs-stay__cell">
                    <span>{{ msg('Guests') }}</span>
                    <b class="tabular">{{ contract.numberOfGuests }}</b>
                  </div>
                  <div v-if="contract.bnbStyleRent && contract.bnbStyleRent.active" class="crs-stay__cell crs-stay__cell--end">
                    <span>{{ $t('roomBoard.sidebar.total') }}</span>
                    <b class="tabular crs-stay__money">{{ contract.bnbStyleRent.totalPayment | numberFormatter }} €</b>
                  </div>
                </div>

                <div class="crs-grid">
                  <section>
                    <div class="crs-label">{{ $t('roomBoard.sidebar.reservation') }}</div>
                    <div class="crs-row"><span>Id</span><b class="tabular crs-row__code" :title="contract.id">{{ contract.id }}</b></div>
                    <div v-if="contract.hostawayReservationId" class="crs-row"><span>External Id</span><b class="tabular crs-row__code">{{ contract.hostawayReservationId }}</b></div>
                    <div class="crs-row"><span>{{ $t('roomBoard.sidebar.bookedOn') }}</span><b class="tabular">{{ $moment(contract.creationTime).format("DD/MM/YY HH:mm") }}</b></div>
                    <div v-if="contract.bnbStyleRent!=null&&contract.bnbStyleRent.active" class="crs-row"><span>{{ msg("Platform") }}</span><b>{{ contract.bnbStyleRent.platform }}</b></div>
                    <div v-if="contract.use" class="crs-row"><span>{{ msg("Usage") }}</span><b>{{ contract.use }}</b></div>
                    <div v-if="contract.landlord" class="crs-row"><span>{{ $t('roomBoard.sidebar.landlord') }}</span><b class="text-capitalize">{{ contract.landlord.firstname }} {{ contract.landlord.lastname }}</b></div>
                    <div v-if="property" class="crs-row"><span>{{ msg("Property") }}</span><b>{{ property.address }} {{ property.house }}, {{ property.city }}</b></div>
                  </section>

                  <section v-if="contract.bnbStyleRent!=null">
                    <div class="crs-label">{{ $t('roomBoard.sidebar.pricing') }}</div>
                    <div v-if="contract.bnbStyleRent.active" class="crs-row crs-row--hero">
                      <span>{{ msg("Period price") }}</span><b class="tabular">{{ contract.bnbStyleRent.totalPayment | numberFormatter }} €</b>
                    </div>
                    <div v-if="contract.bnbStyleRent.active && contract.bnbStyleRent.totalPaymentDiscount" class="crs-row">
                      <span>{{ $t('roomBoard.sidebar.afterDiscount') }}</span><b class="tabular">{{ contract.bnbStyleRent.totalPaymentDiscount | numberFormatter }} €</b>
                    </div>
                    <div v-if="contract.bnbStyleRent.commissionAmount != null" class="crs-row">
                      <span>{{ msg("Platform commision") }}</span><b class="tabular">{{ contract.bnbStyleRent.commissionAmount | numberFormatter }} €</b>
                    </div>
                    <div v-if="contract.bnbStyleRent.paymentProcessingFee != null" class="crs-row">
                      <span>{{ msg("Payment Processing Fee") }}</span><b class="tabular">{{ contract.bnbStyleRent.paymentProcessingFee | numberFormatter }} €</b>
                    </div>
                  </section>
                </div>
              </div>
            </b-tab>

            <!-- Client -->
            <b-tab :title="msg('Client')">
              <div v-if="contract != null" class="crs-tab">
                <div class="crs-grid">
                  <section>
                    <div class="crs-label">{{ $t('roomBoard.sidebar.profile') }}</div>
                    <div class="crs-row">
                      <span>Id</span>
                      <b class="tabular">{{ client.id }}
                        <button type="button" class="crs-link" v-b-tooltip :title="$t('roomBoard.sidebar.openGuestAccount')" @click="openClientAccount">
                          <i class="fa-solid fa-right-to-bracket"/>
                        </button>
                      </b>
                    </div>
                    <div class="crs-row"><span>{{ msg("Register date") }}</span><b class="tabular">{{ client.creationTime }}</b></div>
                    <div class="crs-row"><span>{{ msg("Firstname") }}</span><b class="text-capitalize">{{ contract.client.firstname }}</b></div>
                    <div class="crs-row"><span>{{ msg("Lastname") }}</span><b class="text-capitalize">{{ contract.client.lastname }}</b></div>
                    <div class="crs-row" v-if="contract.client.birthday != null && contract.client.birthday.trim() !== '' && contract.client.birthday !== '1970-01-01'">
                      <span>{{ msg("Birthday") }}</span>
                      <b class="tabular">{{ contract.client.birthday | moment('DD/MM/YY') }} ({{ $moment().diff(contract.client.birthday, 'years') }})</b>
                    </div>
                    <div class="crs-row"><span>{{ msg("Contract Email") }}</span><b class="crs-row__code">{{ contract.client.email }}</b></div>
                    <div class="crs-row"><span>{{ msg("Client Email") }}</span><b class="crs-row__code">{{ client.email }}</b></div>
                    <div class="crs-row"><span>{{ msg("Phone") }}</span><b class="tabular">{{ client.phone }}</b></div>
                  </section>

                  <section>
                    <div v-if="contract.client.address" class="crs-block">
                      <div class="crs-label">{{ msg('address') }}</div>
                      <div class="crs-row"><span>{{ msg("address") }}</span><b>{{ contract.client.address.street }}</b></div>
                      <div class="crs-row"><span>{{ msg("city") }}</span><b>{{ contract.client.address.city }}</b></div>
                      <div class="crs-row"><span>{{ msg("Zip") }}</span><b class="tabular">{{ contract.client.address.zip }}</b></div>
                      <div class="crs-row"><span>{{ msg("country") }}</span><b>{{ contract.client.address.country }}</b></div>
                    </div>

                    <div v-if="contract.income" class="crs-block">
                      <div class="crs-label">{{ $t('roomBoard.sidebar.job') }}</div>
                      <div class="crs-row"><span>{{ msg("Job Title") }}</span><b>{{ contract.income.jobTitle }}</b></div>
                      <div class="crs-row"><span>{{ msg("Company") }}</span><b>{{ contract.income.companyName }}</b></div>
                      <div v-if="contract.income.companyAddress" class="crs-row"><span>{{ msg("Company street") }}</span><b>{{ contract.income.companyAddress.street }}</b></div>
                      <div v-if="contract.income.companyAddress" class="crs-row"><span>{{ msg("Company city") }}</span><b>{{ contract.income.companyAddress.city }}</b></div>
                      <div v-if="contract.income.companyAddress" class="crs-row"><span>{{ msg("Company country") }}</span><b>{{ contract.income.companyAddress.country }}</b></div>
                      <div class="crs-row"><span>{{ msg("Netto income") }}</span><b class="tabular">{{ contract.income.net }}</b></div>
                      <div class="crs-row"><span>{{ msg("Seniority") }}</span><b>{{ contract.income.seniority }}</b></div>
                    </div>

                    <div v-if="contract.card!=null" class="crs-block">
                      <div class="crs-label">{{ $t('roomBoard.sidebar.card') }}</div>
                      <div class="crs-row"><span>{{ msg("Name") }}</span><b>{{ contract.card.name }}</b></div>
                      <div class="crs-row"><span>{{ msg("Number") }}</span><b class="tabular">{{ contract.card.number }}</b></div>
                      <div class="crs-row"><span>{{ msg("Date") }}</span><b class="tabular">{{ contract.card.date }}</b></div>
                      <div class="crs-row"><span>{{ msg("Cvv") }}</span><b class="tabular">{{ contract.card.cvv }}</b></div>
                    </div>
                  </section>
                </div>
              </div>
            </b-tab>

            <!-- Chat -->
            <b-tab :title="msg('Chat')">
              <div v-if="contract != null" class="crs-tab crs-tab--flush">
                <hostaway-chat-component v-bind:contractId="contractId" v-bind:reservation-id="contract.hostawayReservationId" full-height :contract="contract"/>
              </div>
            </b-tab>

            <!-- Payments -->
            <b-tab :title="msg('Payments')">
              <div v-if="contract!=null" class="crs-tab">
                <div class="crs-actions">
                  <button type="button" class="crs-btn crs-btn--primary" @click="openPaymentModal">
                    <i class="fas fa-credit-card mr-25"/>{{ msg('Make payment') }}
                  </button>
                </div>
                <payment-client-table @submitted="reloadContractToPayment" :payments="contract.clientPayments" :contract-id="contract.id"/>
              </div>
            </b-tab>

            <!-- Damages -->
            <b-tab :title="msg('Damages')">
              <div v-if="contract != null" class="crs-tab">
                <contract-damage-component v-bind:contract-id="contract.id"/>
              </div>
            </b-tab>

            <!-- Client activity (lazy: only fetches when opened) -->
            <b-tab :title="$t('clientActivity.tab')" lazy>
              <div v-if="client != null" class="crs-tab">
                <client-activity-timeline :client-id="client.id" scrollable/>
              </div>
            </b-tab>
          </b-tabs>
        </div>

        <!-- loading -->
        <div v-else>
          <b-tabs v-model="tabsIndex" nav-class="crs-nav">
            <b-tab :title="msg('Contract')" active></b-tab>
            <b-tab :title="msg('Client')"></b-tab>
            <b-tab :title="msg('Chat')"></b-tab>
            <b-tab :title="msg('Payments')"></b-tab>
            <b-tab :title="msg('Damages')"></b-tab>
          </b-tabs>
          <div class="crs-tab">
            <b-skeleton-table :rows="12" :columns="2"></b-skeleton-table>
          </div>
        </div>
      </b-sidebar>
    </portal>

    <!-- Payment Modal -->
    <b-modal v-if="contract != null" no-fade v-model="dialogs.payment" size="l" ref="payment-modal" id="payment-modal"
             :hide-footer="true" :title="msg('Make payment') + ' — ' + contract.client.firstname + ' ' + contract.client.lastname"
             no-close-on-backdrop centered modal-class="crs-pay-modal">
      <div class="crs-pay">
        <label class="crs-label">{{ msg("Type") }}</label>
        <v-select v-model="paymentType" class="mb-1" :clearable="false" :options="paymentOptions" label="label" :reduce="(b) => b.value" placeholder="Select payment type"></v-select>
        <template v-if="paymentType === 'Credit card'">
          <label class="crs-label">{{ msg("Reason") }}</label>
          <b-form-input v-model="reason" class="mb-1"></b-form-input>
          <label class="crs-label">{{ msg("Amount") }}</label>
          <b-form-input v-model="paymentAmount" type="number" step="0.01" min="0" max="3000" class="mb-1"></b-form-input>
          <button type="button" class="crs-btn crs-btn--primary crs-btn--wide"
                  :disabled="paymentAmount == null || paymentAmount <= 0.0 || paymentLoading || reason == null" @click="makePayment">
            <i class="fas fa-bolt mr-25"/>{{ msg('Charge the customer now') }}
          </button>
        </template>
        <template v-else-if="paymentType === 'Cash' || paymentType === 'Wire transfer'">
          <label class="crs-label">{{ msg("Reason") }}</label>
          <b-form-input v-model="reason" class="mb-1"></b-form-input>
          <label class="crs-label">{{ msg("Amount") }}</label>
          <b-form-input v-model="paymentAmount" type="number" step="0.01" min="0" class="mb-1"></b-form-input>
          <b-form-checkbox class="mb-1" v-model="isPaid" switch>{{ isPaid ? msg("Paid") : msg("Not paid") }}</b-form-checkbox>
          <b-form-datepicker v-model="paymentDate" :min="minDate" placeholder="Select date" class="mb-1"></b-form-datepicker>
          <button type="button" class="crs-btn crs-btn--primary crs-btn--wide"
                  :disabled="!paymentDate || paymentAmount == null || paymentAmount <= 0.0 || paymentLoading || reason == null" @click="makePayment">
            <i class="fas fa-check mr-25"/>{{ msg('Confirm Payment') }}
          </button>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations} from "vuex";
import HostawayChatComponent from "@/views/realEstate/contracts/HostawayChatComponent.vue";
import PaymentClientTable from "@/views/realEstate/contracts/paymentClientTable.vue";
import ContractDamageComponent from "@/views/realEstate/contracts/ContractDamageComponent.vue";
import ClientActivityTimeline from "@/components/panels/client/ClientActivityTimeline.vue";

export default {
  name: "ContractRentSidebar",
  components: {ContractDamageComponent, PaymentClientTable, HostawayChatComponent, ClientActivityTimeline},
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) { this.getContract(); }
      }
    },
    'sidebar.visible'() {
      if (this.isBodyScrollDisabled !== this.sidebar.visible) {
        this.DISABLE_BODY_SCROLL(this.sidebar.visible);
      }
    }
  },
  data: () => ({
    loading: null,
    sidebar: { visible: false },
    dialogs: {sms: false, payment: false},
    tabsIndex: 0,
    contractId: null,
    contract: null,
    property: null,
    client: null,
    permissions: null,
    paymentAmount: 0.0,
    reason: null,
    paymentLoading: false,
    paymentType: null,
    isPaid: false,
    paymentDate: null,
  }),
  computed: {
    ...mapGetters("appConfig", ["isBodyScrollDisabled"]),
    minDate() { return new Date().toISOString().split('T')[0]; },
    paymentOptions() {
      return [
        {label: this.msg('Credit Card'), value: 'Credit card'},
        {label: this.msg('Cash'), value: 'Cash'},
        {label: this.msg('Wire Transfer'), value: 'Wire transfer'}
      ];
    },
    /** display only: the status word's color in the header */
    statusTone() {
      const status = ((this.contract && this.contract.status) || '').toUpperCase();
      if (status.includes('ACTIV')) return 'ok';
      if (status.includes('CANCEL')) return 'bad';
      return 'mute';
    },
  },
  methods: {
    ...mapActions("realEstateContracts", ["createPdfInvoiceContract", "getById", "updateContract", "sproofRequest", "cancelContract", "changeContract", "updateStatus", "updateFilesData", "updateApproveStatus", "changeContractOwner", "updateEditableFields", "updateDocumentFile", "generateAndSendLink", "sendCheckInDetails", "getMonetaryId"]),
    ...mapActions("clients", ["generateToken"]),
    ...mapMutations('appConfig', ['DISABLE_BODY_SCROLL']),
    ...mapActions("monetary", ["chargeCustomer"]),
    makePayment() {
      let $this = this;
      this.paymentLoading = true
      this.chargeCustomer({contractId: this.contract.id, amount: this.paymentAmount, reason: this.reason, type: this.paymentType, paid: this.isPaid, date: this.paymentDate}).then(approve => {
        $this.dialogs.payment = false
      }).finally(() => {
        $this.paymentLoading = false; $this.paymentAmount = 0.0; $this.reason = null; $this.paymentType = null; $this.isPaid = false; $this.paymentDate = null; $this.dialogs.payment = false; $this.reloadContractToPayment();
      })
    },
    openPaymentModal() { this.dialogs.payment = true },
    reloadContractToPayment() {
      this.sidebar.visible = false;
      this.sidebar.visible = true;
      this.tabsIndex = 3;
      this.getContract();
    },
    showContract(contractId) {
      this.contractId = contractId;
      this.sidebar.visible = true;
      this.tabsIndex = 0;
      this.getContract();
    },
    getContract() {
      if (this.contractId == null || this.contractId === "") return;
      let $this = this;
      let contractId = this.contractId;
      this.loading = contractId;
      this.getById(this.contractId).then(this.onContractReceive, this.onContractRetrieveError).finally(() => { if ($this.loading === contractId) { $this.loading = null } });
    },
    onContractReceive(data) {
      let $this = this;
      this.contract = data.contract;
      this.contract.statusHistories = this.contract.statusHistories.sort((a, b) => $this.$moment(b.time, 'YYYY-MM-DD HH:mm:ss').diff($this.$moment(a.time, 'YYYY-MM-DD HH:mm:ss')))
      this.property = data.property;
      this.client = data.client;
      this.permissions = data.permissions;
      this.allContractStatus = data.statuses;
    },
    openClientAccount() {
      this.generateToken({clientId: this.client.id, contractId: this.contract.id})
        .then(res => { if (res && res.details && res.details.sitePath) window.open(res.details.sitePath, '_blank'); })
    }
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/*
 * Same flat command-center language as the room board: hairlines between
 * zones, uppercase micro-labels, values as typography, ONE accent color.
 */

/* ── header ── */
.crs-head {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;
  padding: 16px 20px 13px;
  border-bottom: 1px solid rgba(23, 30, 48, .08);
}
.crs-head__main { min-width: 0; }
.crs-head__top { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.crs-head__name { font-size: 1.3rem; font-weight: 800; color: #171e30; letter-spacing: -.015em; line-height: 1.15; text-transform: capitalize; }
.crs-head__status {
  font-size: .64rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase;
  &--ok { color: #2fa46a; }
  &--bad { color: #e05252; }
  &--mute { color: #8a93a5; }
}
.crs-head__sub {
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px 14px; margin-top: 4px;
  font-size: .74rem; font-weight: 600; color: #9aa2b1;
}
.crs-head__tools { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.crs-tool {
  width: 32px; height: 32px; border: 1px solid rgba(23, 30, 48, .12); border-radius: 7px; cursor: pointer;
  background: transparent; color: #7c8598; font-size: .76rem;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all .12s;
  &:hover { border-color: #3b6ff6; color: #3b6ff6; text-decoration: none; }
  &:focus { outline: none; }
}

/* ── tabs ── */
:deep(.crs-nav) {
  border-bottom: 1px solid rgba(23, 30, 48, .08);
  flex-wrap: nowrap; overflow-x: auto; overflow-y: hidden;
  padding: 0 12px; gap: 2px;
  .nav-item { flex-shrink: 0; }
  .nav-link {
    border: none !important; border-radius: 0; background: transparent !important;
    font-size: .78rem; font-weight: 700; color: #7c8598;
    padding: 11px 13px; white-space: nowrap;
    transition: color .12s;
    &:hover { color: #171e30; }
    &.active { color: #171e30; box-shadow: inset 0 -2px 0 #3b6ff6; }
  }
}

.crs-tab { padding: 18px 20px 22px; }
.crs-tab--flush { padding: 10px 14px 14px; }
.crs-actions { display: flex; justify-content: flex-end; margin-bottom: 14px; }

/* ── stay strip ── */
.crs-stay {
  display: flex; align-items: center; flex-wrap: wrap; gap: 14px 26px;
  padding-bottom: 16px; margin-bottom: 16px;
  border-bottom: 1px solid rgba(23, 30, 48, .08);
}
.crs-stay__cell {
  display: flex; flex-direction: column; min-width: 0;
  span { font-size: .6rem; font-weight: 800; text-transform: uppercase; letter-spacing: .1em; color: #9aa2b1; }
  b { font-size: .96rem; font-weight: 800; color: #171e30; margin-top: 2px; }
  small { font-size: .66rem; color: #9aa2b1; }
  &--end { margin-left: auto; text-align: right; }
}
.crs-stay__arrow { color: #b3bac6; font-size: .74rem; }
.crs-stay__money { font-size: 1.12rem !important; }

/* ── info grid ── */
.crs-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 6px 48px;
  section { min-width: 0; }
}
.crs-block { margin-bottom: 18px; &:last-child { margin-bottom: 0; } }
.crs-label {
  font-size: .62rem; font-weight: 800; text-transform: uppercase; letter-spacing: .12em;
  color: #9aa2b1; margin-bottom: 6px;
}
.crs-row {
  display: flex; align-items: baseline; justify-content: space-between; gap: 14px;
  padding: 7px 0; min-width: 0;
  border-bottom: 1px solid rgba(23, 30, 48, .05);
  &:last-child { border-bottom: none; }
  span { font-size: .68rem; font-weight: 600; color: #9aa2b1; white-space: nowrap; }
  b {
    font-size: .8rem; font-weight: 600; color: #2a3348; text-align: right; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  &--hero b { font-size: 1.02rem; font-weight: 800; color: #171e30; }
}
.crs-row__code { font-size: .72rem !important; letter-spacing: .01em; }
.crs-link {
  border: none; background: none; padding: 0 0 0 6px; cursor: pointer;
  color: #3b6ff6; font-size: .74rem;
  &:hover { color: #2a56c8; }
  &:focus { outline: none; }
}

/* ── buttons ── */
.crs-btn {
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid rgba(23, 30, 48, .14); border-radius: 7px; background: transparent;
  color: #4a5468; font-size: .76rem; font-weight: 700; cursor: pointer;
  padding: 8px 14px; white-space: nowrap; transition: all .12s;
  &:hover { border-color: #171e30; color: #171e30; }
  &:focus { outline: none; }
  &:disabled { opacity: .5; cursor: default; }
  &--primary {
    background: #171e30; border-color: #171e30; color: #fff;
    &:hover { background: #232d49; border-color: #232d49; color: #fff; }
  }
  &--wide { width: 100%; margin-top: 6px; }
}

/* ── payment modal ── */
.crs-pay { padding: 4px 2px 6px; }
.crs-pay .crs-label { margin-top: 10px; }
.crs-pay .crs-label:first-child { margin-top: 0; }

/* ── mobile ── */
@media (max-width: 768px) {
  .crs-head { padding: 12px 14px 10px; }
  .crs-head__name { font-size: 1.08rem; }
  .crs-head__sub { font-size: .7rem; gap: 2px 10px; }
  .crs-tab { padding: 14px 14px 18px; }
  .crs-tab--flush { padding: 8px 10px 12px; }
  .crs-stay { gap: 12px 18px; }
  .crs-stay__arrow { display: none; }
  .crs-stay__cell--end { margin-left: 0; text-align: left; flex-basis: 100%; }
  .crs-grid { grid-template-columns: 1fr; gap: 14px 0; }
  .crs-row__code { white-space: normal; word-break: break-all; }
  :deep(.crs-nav) { padding: 0 6px; .nav-link { padding: 10px 10px; font-size: .74rem; } }
}
</style>

<style lang="scss">
/* the sidebar itself: never wider than the screen */
.crs-panel {
  max-width: 100vw !important;
}

.dark-layout {
  .crs-head { border-color: rgba(255, 255, 255, .08); }
  .crs-head__name { color: #eef1f7; }
  .crs-tool { border-color: rgba(255, 255, 255, .16); color: #9aa2b1; &:hover { border-color: #7ba3ff; color: #7ba3ff; } }
  .crs-nav {
    border-color: rgba(255, 255, 255, .08) !important;
    .nav-link { color: #8a93a5; &:hover { color: #eef1f7; } &.active { color: #eef1f7; box-shadow: inset 0 -2px 0 #5b8bff; } }
  }
  .crs-stay { border-color: rgba(255, 255, 255, .08); }
  .crs-stay__cell b { color: #eef1f7; }
  .crs-row { border-color: rgba(255, 255, 255, .06); b { color: #cfd5e1; } }
  .crs-row--hero b { color: #eef1f7; }
  .crs-link { color: #7ba3ff; }
  .crs-btn { border-color: rgba(255, 255, 255, .16); color: #aeb6c6; &:hover { border-color: #eef1f7; color: #eef1f7; } }
  .crs-btn--primary {
    background: #eef1f7; border-color: #eef1f7; color: #171e30;
    &:hover { background: #fff; border-color: #fff; color: #171e30; }
  }
}
</style>
